import { User } from 'lucide-react'
import { Suspense } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { VerificationBadge } from 'src/shared/components/icons/verification-badge'
import { Avatar, AvatarFallback, AvatarImage } from 'src/shared/components/ui/miscellaneous/avatar'
import { useUserByIdSuspense } from 'src/shared/features/users/api/accounts/use-user-by-id'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import type { Invite } from 'src/shared/schemas/invites/invite'
import { cn } from 'src/shared/utils/cn'

import InviteCardSkeleton from '../skeletons/invite-card'

interface AcceptedUserProps {
  userId: number
  invite: Invite
  variant?: 'light' | 'dark'
  onOpenChange?: (open: boolean) => void
}

export const AcceptedUserInner = ({ userId, invite, variant = 'light', onOpenChange }: AcceptedUserProps) => {
  const { data: user } = useUserByIdSuspense(userId)
  const navigate = useNavigate()

  if (!user) return null

  const [firstName, ...lastNameParts] = user.fullName.split(' ')
  const lastName = lastNameParts.join(' ')

  const handleClick = () => {
    onOpenChange?.(false)
    navigate(paths.users.profile.getHref(user.username))
  }

  return (
    <button
      onClick={handleClick}
      className="flex w-full items-center gap-3 py-2 text-left transition-opacity hover:opacity-80"
    >
      <Avatar className="size-11 bg-primary">
        <AvatarImage src={user.profilePhoto} alt={user.fullName} className="size-11" />
        <AvatarFallback>
          <User className="size-7" />
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-col">
        <div className="flex min-w-0 flex-wrap items-center gap-1 text-[15px] font-bold">
          <span className={cn('break-words', variant === 'dark' && 'text-white')}>{firstName}</span>
          <span className={cn('flex items-center gap-1 whitespace-nowrap', variant === 'dark' && 'text-white')}>
            {lastName}
            <VerificationBadge isVerified={invite.completed} width={16} height={16} />
          </span>
        </div>
        <span className={cn('text-[13px] text-muted', variant === 'dark' && 'text-white/65')}>
          {professionMap[user.profession].label}
        </span>
      </div>
    </button>
  )
}

export const AcceptedUser = (props: AcceptedUserProps) => {
  return (
    <Suspense fallback={<InviteCardSkeleton />}>
      <AcceptedUserInner {...props} />
    </Suspense>
  )
}
